/* eslint-disable react/prop-types */
import {
  Alert,
  Card,
  Col,
  Divider,
  Layout,
  Row,
  Statistic,
  Typography,
  Select,
  Spin,
} from "antd";
import React, { useState, useEffect, useMemo, useCallback } from "react";
import { Link } from "react-router-dom";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Tooltip,
  Legend,
  Filler,
} from "chart.js";
import { Line } from "react-chartjs-2";
import { getDistributionTransactions } from "../../../api/distributionTransactions";
import { getPlatforms } from "../../../api/distributionProject";
import functions from "../../../components/utils/functions";
import { projectHasYoutubePlatform } from "../../../components/utils/youtubePlatform";

const { Title } = Typography;

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Tooltip,
  Legend,
  Filler
);

const month = [
  "January",
  "February",
  "March",
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December",
];

const accum = (arr, key) =>
  Object.values(arr).length
    ? Object.values(arr)
        .map((item) => item.reduce((n, data) => n + data[key], 0))
        .reduce((a, b) => a + b, 0)
    : 0;

const dataArr = [
  { key: "views", title: "Total Streams" },
  { key: "users", title: "Total Streaming Users" },
  { key: "time", title: "Total Stream Duration (min)" },
];

const chartAxisColor = "#a3a3a3";
const chartGridColor = "#262626";

const options = {
  responsive: true,
  plugins: {
    legend: {
      position: "top",
      labels: { color: chartAxisColor },
    },
    title: { display: false },
  },
  scales: {
    x: {
      ticks: {
        color: chartAxisColor,
        autoSkip: true,
        maxRotation: 45,
        minRotation: 0,
      },
      grid: { color: chartGridColor },
    },
    y: {
      ticks: { color: chartAxisColor },
      grid: { color: chartGridColor },
    },
  },
};

// 🔍 Helper to parse "June 2024" into Date
const parseMonthYear = (monthStr) => {
  const [mon, year] = monthStr.split(" ");
  const monthIndex = month.indexOf(mon);
  return new Date(parseInt(year), monthIndex, 1);
};

// 🔍 Get earliest month from metric data
const getEarliestMonth = (data) => {
  const allDates = Object.values(data)
    .flat()
    .map((entry) => parseMonthYear(entry.month));

  return allDates.length
    ? new Date(Math.min(...allDates.map((d) => d.getTime())))
    : new Date(); // fallback
};

// 🔁 Generate all months from start to end
const generateMonthLabels = (start, end) => {
  const labels = [];
  const current = new Date(start.getFullYear(), start.getMonth(), 1);
  const endDate = new Date(end.getFullYear(), end.getMonth(), 1);

  while (current <= endDate) {
    labels.push(month[current.getMonth()] + " " + current.getFullYear());
    current.setMonth(current.getMonth() + 1);
  }

  return labels;
};

const MONTH_RANGE_OPTIONS = [
  { value: "6", label: "Last 6 months" },
  { value: "12", label: "Last 12 months" },
  { value: "all", label: "All time" },
];

const Metric = ({ distributionProjectId, distribution }) => {
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedMonthRange, setSelectedMonthRange] = useState("6");
  const [selectedPlatformId, setSelectedPlatformId] = useState("all");
  const [platforms, setPlatforms] = useState({});

  useEffect(() => {
    getPlatforms().then((rows) => {
      const m = {};
      (rows || []).forEach((p) => {
        m[p.platform_id] = {
          title: p.display_name || p.platform_id,
          color: p.chart_color || "#888888",
        };
      });
      setPlatforms(m);
    });
  }, []);

  const resolvePlatform = useCallback(
    (id) => {
      if (platforms[id]) return platforms[id];
      const fromDist = distribution?.platform?.find((x) => x.id === id);
      if (fromDist?.display_name) {
        return {
          title: fromDist.display_name,
          color: fromDist.chart_color || "#888888",
        };
      }
      const txRow = transactions.find((r) => r.platform_id === id);
      if (txRow?.platform_display_name) {
        return { title: txRow.platform_display_name, color: "#888888" };
      }
      return { title: id, color: "#888888" };
    },
    [platforms, distribution?.platform, transactions]
  );

  const validPlatformIds =
    distribution && distribution.platform
      ? distribution.platform
          .filter((item) => ["live", "offline"].includes(item.status))
          .map((item) => item.id)
      : [];

  const hasYoutubePlatform = useMemo(
    () => projectHasYoutubePlatform(distribution?.platform),
    [distribution?.platform]
  );

  useEffect(() => {
    if (!distributionProjectId) {
      setLoading(false);
      return;
    }
    setLoading(true);
    let cancelled = false;
    const filters = {
      months: selectedMonthRange,
      platformId: selectedPlatformId === "all" ? undefined : selectedPlatformId,
    };
    getDistributionTransactions(distributionProjectId, filters).then((rows) => {
      if (!cancelled) {
        setTransactions(rows);
        setLoading(false);
      }
    });
    return () => {
      cancelled = true;
    };
  }, [distributionProjectId, selectedMonthRange, selectedPlatformId]);

  const metric = {};
  const revenue = {};
  transactions.forEach((row) => {
    if (!validPlatformIds.includes(row.platform_id)) return;
    if (!metric[row.platform_id]) metric[row.platform_id] = [];
    metric[row.platform_id].push({
      month: row.month_label,
      views: Number(row.views) || 0,
      time: Math.round((Number(row.watch_time_seconds) || 0) / 60),
      users: Number(row.users) || 0,
    });
    if (!revenue[row.platform_id]) revenue[row.platform_id] = [];
    revenue[row.platform_id].push({
      month: row.month_label,
      revenue: Number(row.revenue),
    });
  });

  const validRevArr = validPlatformIds.filter(
    (id) => revenue[id] && revenue[id].length > 0
  );

  const currentDate = new Date();
  const earliestMetricDate = getEarliestMonth(metric);
  const labels = generateMonthLabels(earliestMetricDate, currentDate);

  const platformIdsWithDataOption = [
    ...new Set(transactions.map((r) => r.platform_id).filter(Boolean)),
  ].filter((id) => validPlatformIds.includes(id));

  const activePlatformIds =
    selectedPlatformId === "all" || !selectedPlatformId
      ? platformIdsWithDataOption
      : platformIdsWithDataOption.includes(selectedPlatformId)
      ? [selectedPlatformId]
      : platformIdsWithDataOption;

  const platformOptions = useMemo(() => {
    const base = [
      { value: "all", label: "All platforms" },
      ...platformIdsWithDataOption.map((id) => ({
        value: id,
        label: resolvePlatform(id).title,
      })),
    ];
    if (
      selectedPlatformId &&
      selectedPlatformId !== "all" &&
      !base.some((o) => o.value === selectedPlatformId)
    ) {
      base.push({
        value: selectedPlatformId,
        label: resolvePlatform(selectedPlatformId).title,
      });
    }
    return base;
  }, [platformIdsWithDataOption, resolvePlatform, selectedPlatformId]);

  const newArr = {};
  const newRevArr = {};

  Object.keys(metric).forEach((x) => {
    newArr[x] = labels.map((label) =>
      metric[x].some((data) => data.month === label)
        ? metric[x]
            .filter((data) => data.month === label)
            .map((item) => ({
              views: item.views,
              time: item.time,
              users: item.users,
            }))[0]
        : { views: 0, time: 0, users: 0 }
    );
  });

  Object.keys(revenue).forEach((x) => {
    newRevArr[x] = labels.map((label) =>
      revenue[x].some((data) => data.month === label)
        ? revenue[x]
            .filter((data) => data.month === label)
            .map((item) => ({ revenue: item.revenue }))[0]
        : { revenue: 0 }
    );
  });

  const filteredNewArr = {};
  const filteredNewRevArr = {};
  activePlatformIds.forEach((x) => {
    if (newArr[x]) filteredNewArr[x] = newArr[x];
    if (newRevArr[x]) filteredNewRevArr[x] = newRevArr[x];
  });

  const plat = resolvePlatform;

  const data = {
    labels,
    datasets: dataArr.map((item) =>
      Object.keys(filteredNewArr).map((x) => ({
        label: plat(x).title,
        data: filteredNewArr[x].map((data) => data[item.key] || 0),
        borderColor: plat(x).color,
        backgroundColor: plat(x).color,
        fill: false,
      }))
    ),
  };

  const revData = {
    labels,
    datasets: Object.keys(filteredNewRevArr).map((x) => ({
      label: plat(x).title,
      data: filteredNewRevArr[x].map((data) => data["revenue"] || 0),
      borderColor: plat(x).color,
      backgroundColor: plat(x).color,
      fill: false,
    })),
  };

  const sum__total_streams = accum(filteredNewArr, "views");
  const sum__total_streaming_users = accum(filteredNewArr, "users");
  const sum__stream_duration = accum(filteredNewArr, "time");
  const sum__share_revenue = accum(filteredNewRevArr, "revenue");

  const screenWidth = window ? window.innerWidth : 0;

  const isInitialLoad = loading && transactions.length === 0;
  const isFilterLoading = loading && transactions.length > 0;

  if (isInitialLoad) {
    return (
      <>
        <Divider />
        <Row>
          <Col xs={{ span: 24 }} lg={{ span: 12 }}>
            <Title level={4}>Streaming Analytics</Title>
          </Col>
        </Row>
        <Layout className="vc-panel" style={{ padding: 8 }}>
          <Row gutter={16} style={{ padding: "8px" }}>
            <Col span={24}>Loading metrics…</Col>
          </Row>
        </Layout>
      </>
    );
  }

  const StatisticsCard = ({ screenWidth, title, slug }) => (
    <Col
      xs={{ span: validRevArr.length > 0 ? 12 : 8 }}
      lg={{ span: validRevArr.length > 0 ? 6 : 8 }}
      style={{
        display: screenWidth > 768 ? "block" : "flex",
        marginBottom: "16px",
      }}
    >
      <Card style={{ width: "100%", height: "100%" }}>
        <Statistic title={title} value={slug} />
      </Card>
    </Col>
  );

  return (
    <>
      {hasYoutubePlatform && (
        <Alert
          type="info"
          showIcon
          style={{ marginBottom: 16 }}
          message="Why YouTube revenue is not updated?"
          description={
            <>
              The chart below is your <strong>settled</strong> streaming revenue
              from partners in <strong>₹ (INR)</strong>, after we have recorded
              payouts in our system. For estimates, visit{" "}
              <Link
                className="vc-alert-router-link"
                to={`/project/${distributionProjectId}/youtube-analytics`}
              >
                YouTube revenue (estimates)
              </Link>
              .
            </>
          }
        />
      )}
      <Divider />
      <Row gutter={[16, 16]} align="middle">
        <Col xs={{ span: 24 }} md={{ span: 12 }}>
          <Title level={4} style={{ margin: 0 }}>
            Streaming Analytics
          </Title>
        </Col>
        <Col xs={{ span: 24 }} md={{ span: 12 }}>
          <Row gutter={16} align="middle">
            <Col flex="none">
              <Select
                value={selectedMonthRange}
                onChange={setSelectedMonthRange}
                options={MONTH_RANGE_OPTIONS}
                style={{ minWidth: 160 }}
                placeholder="Month range"
              />
            </Col>
            <Col flex="none">
              <Select
                value={selectedPlatformId}
                onChange={setSelectedPlatformId}
                options={platformOptions}
                style={{ minWidth: 180 }}
                placeholder="Platform"
              />
            </Col>
          </Row>
        </Col>
      </Row>
      <Layout className="vc-panel" style={{ position: "relative", padding: 8 }}>
        {isFilterLoading && (
          <div
            style={{
              position: "absolute",
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              background: "rgba(0,0,0,0.45)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              zIndex: 10,
              borderRadius: 4,
            }}
          >
            <Spin size="large" tip="Updating…" />
          </div>
        )}
        <Row gutter={16} style={{ padding: "8px" }}>
          {validRevArr.length > 0 && (
            <StatisticsCard
              title="Net Revenue"
              slug={`₹ ${functions.formatIndianNumber(
                parseFloat(sum__share_revenue),
                { minimumFractionDigits: 2, maximumFractionDigits: 2 }
              )}`}
            />
          )}
          <StatisticsCard
            title="Total Streams"
            slug={functions.formatIndianNumber(sum__total_streams)}
          />
          <StatisticsCard
            title="Total Streaming Users"
            slug={functions.formatIndianNumber(sum__total_streaming_users)}
          />
          <StatisticsCard
            title="Total Stream Duration (min)"
            slug={functions.formatIndianNumber(sum__stream_duration)}
          />
        </Row>
        <br />
        <br />
        {distribution && distribution.platform && (
          <>
            {validRevArr.length > 0 && (
              <>
                <Title level={5}>Revenue (INR)</Title>
                <Line
                  options={options}
                  data={revData}
                  height={screenWidth > 768 ? "30%" : "50%"}
                  width="100%"
                />
              </>
            )}
            {data.datasets.map((item, index) => (
              <React.Fragment key={dataArr[index].title}>
                <Title level={5} style={{ marginTop: 64 }}>
                  {dataArr[index].title}
                </Title>
                <Line
                  options={options}
                  data={{ labels, datasets: item }}
                  height={screenWidth > 768 ? "30%" : "50%"}
                  width="100%"
                />
              </React.Fragment>
            ))}
          </>
        )}
      </Layout>
    </>
  );
};

export default Metric;
